import { PossibleClientActionNames, type GambleOfferDetails } from "@slot/client-engine";
import type { BalanceStore } from "../balance/BalanceStore";
import type { CurrentWinStore } from "../win/current-win/CurrentWinStore";
import type { GambleSession } from "./GambleSession";
import type { GambleFlowResult } from "./GambleTypes";
import type { Log } from "@slot/presentation";

export interface GambleOfferFlowDeps {
  session: GambleSession;
  currentWinStore: CurrentWinStore;
  balanceStore: BalanceStore;
  log: Log;
}

/** A server-offered gamble on the settled round: whether the offer stands, and the flow when the player takes it. */
export class GambleOfferFlow {
  public constructor(private readonly deps: GambleOfferFlowDeps) {}

  /**
   * The offer stands when the server lists "gamble" and offers a positive amount to stake (decision 29); the
   * snapshot validator has already checked the amount is whole minor units. Anything else is a logged skip
   * (decision 23): no gamble entry shows.
   */
  public shouldOffer(roundSnapshot: { possibleClientActions?: string[]; gambleOfferDetails?: GambleOfferDetails }): boolean {
    if (!(roundSnapshot.possibleClientActions ?? []).includes(PossibleClientActionNames.gamble)) return false;
    // Optional chaining, not a check on the offer: the validator lets a null offer through as no offer.
    const offeredMinor = roundSnapshot.gambleOfferDetails?.currentWinAmountMinor;
    if (offeredMinor !== undefined && BigInt(offeredMinor) > 0n) return true;
    this.deps.log(`[GambleOffer] not offered — "gamble" listed, offered amount ${offeredMinor ?? "none"}`);
    return false;
  }

  /**
   * Stakes the server's offered amount, gambleOfferDetails.currentWinAmountMinor: the win the settlement credited and
   * the only stake the backend accepts (decision 29). The win readout never sets it. Afterwards the server's final
   * balance applies and the win readout shows the gamble's result: none after a loss, the collected amount after a
   * collect.
   */
  public async run(input: { roundIdentifier: string; possibleClientActions: string[]; gambleOfferDetails?: GambleOfferDetails }): Promise<GambleFlowResult | null> {
    this.deps.log(`[GambleOffer] start round=${input.roundIdentifier}`);
    // shouldOffer recorded the offer with its details; the pending offer's type leaves them optional.
    const offer = input.gambleOfferDetails;
    if (!offer) {
      this.deps.log("[GambleOffer] aborted — the server sent no gambleOfferDetails");
      return null;
    }
    const result = await this.deps.session.runGambleFlow({
      roundIdentifier: input.roundIdentifier,
      possibleClientActions: input.possibleClientActions,
      gambleOfferDetails: offer,
    });
    if (result.finalBalanceSnapshot?.balanceAmount) this.deps.balanceStore.setBalanceMinor(result.finalBalanceSnapshot.balanceAmount);
    // Written before any await: the overlay has already released the bar, which must not paint the staked win.
    // A loss forfeits the whole stake (double or nothing), so the lost branch never reads the amount.
    if (result.outcome === "lost") this.deps.currentWinStore.clear();
    else this.deps.currentWinStore.setWinMinor(result.finalWinAmountMinor);
    this.deps.log(`[GambleOffer] complete outcome=${result.outcome} count=${result.gambleCount}`);
    return result;
  }
}
